import { onScopeDispose, reactive } from 'vue'

// Speed is measured over a sliding window so it reacts to stalls (e.g. a slow storage volume pushing back) without
// jumping around on every progress event.
const SPEED_WINDOW_MS = 3000
const TICK_MS = 250
const HISTORY_LENGTH = 40

type Sample = { at: number, loaded: number }

export type UploadSpeedStats = {
    active: boolean
    loaded: number
    total: number
    bytesPerSecond: number
    averageBytesPerSecond: number
    secondsRemaining: number | null
    /** Seconds since every byte was sent, while the server is still busy storing/processing it. */
    serverSeconds: number | null
    history: number[]
}

export function formatUploadBytes(bytes: number): string {
    if (bytes < 1024) return `${ Math.round(bytes) } B`
    const units = ['KB', 'MB', 'GB']
    let value = bytes / 1024
    let unit = 0
    while (value >= 1024 && unit < units.length - 1) {
        value /= 1024
        unit++
    }
    return `${ value >= 100 ? value.toFixed(0) : value.toFixed(1) } ${ units[unit] }`
}

export function formatUploadDuration(seconds: number): string {
    const rounded = Math.max(0, Math.ceil(seconds))
    if (rounded < 60) return `${ rounded }s`
    const minutes = Math.floor(rounded / 60)
    if (minutes < 60) return `${ minutes }m ${ String(rounded % 60).padStart(2, '0') }s`
    return `${ Math.floor(minutes / 60) }h ${ String(minutes % 60).padStart(2, '0') }m`
}

/**
 * Tracks XHR upload progress and derives live transfer speed, ETA and a short speed history for a sparkline.
 * Call `start()` before `xhr.send`, pass `progress` to `xhr.upload`'s progress listener and `stop()` when done.
 */
export function useUploadSpeed() {
    const stats = reactive<UploadSpeedStats>({
        active: false,
        loaded: 0,
        total: 0,
        bytesPerSecond: 0,
        averageBytesPerSecond: 0,
        secondsRemaining: null,
        serverSeconds: null,
        history: [],
    })

    let samples: Sample[] = []
    let startedAt = 0
    let sentAt: number | null = null
    let timer: ReturnType<typeof setInterval> | undefined

    const tick = () => {
        const now = performance.now()
        // Drop samples that left the window, but keep the newest of those as the baseline.
        while (samples.length > 1 && now - samples[1]!.at >= SPEED_WINDOW_MS) samples.shift()
        const baseline = samples[0]!
        const latest = samples.at(-1)!
        // Measuring up to `now` (not the latest sample) lets the speed fall toward zero when progress stalls.
        const windowSeconds = (now - baseline.at) / 1000
        stats.bytesPerSecond = windowSeconds > 0 ? (latest.loaded - baseline.loaded) / windowSeconds : 0

        const elapsed = (now - startedAt) / 1000
        stats.averageBytesPerSecond = elapsed > 0 ? stats.loaded / elapsed : 0
        const remaining = stats.total - stats.loaded
        const etaSpeed = stats.bytesPerSecond || stats.averageBytesPerSecond
        stats.secondsRemaining = stats.total && remaining > 0 && etaSpeed > 0 ? remaining / etaSpeed : null
        stats.serverSeconds = sentAt === null ? null : (now - sentAt) / 1000

        if (sentAt === null) {
            stats.history.push(stats.bytesPerSecond)
            if (stats.history.length > HISTORY_LENGTH) stats.history.shift()
        }
    }

    const stop = () => {
        if (timer) clearInterval(timer)
        timer = undefined
        stats.active = false
    }

    const start = () => {
        stop()
        samples = []
        startedAt = performance.now()
        sentAt = null
        Object.assign(stats, {
            active: true,
            loaded: 0,
            total: 0,
            bytesPerSecond: 0,
            averageBytesPerSecond: 0,
            secondsRemaining: null,
            serverSeconds: null,
            history: [],
        })
        samples.push({ at: startedAt, loaded: 0 })
        timer = setInterval(tick, TICK_MS)
    }

    const progress = (event: ProgressEvent) => {
        if (!event.lengthComputable) return
        const now = performance.now()
        stats.loaded = event.loaded
        stats.total = event.total
        samples.push({ at: now, loaded: event.loaded })
        if (event.loaded >= event.total && sentAt === null) {
            sentAt = now
            stats.secondsRemaining = null
        }
    }

    onScopeDispose(stop)

    return { stats, start, progress, stop }
}
